// =====================================================
// SmartphoneDB.jsx — Smartphone-Katalog & Datenbank
// Erweitert mit vollständigen Specs, Infoblatt & Print
// =====================================================
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';

function fmtEuro(v) { return (v || 0).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' }); }

function GlassCard({ children, glow = 'cyan', style = {}, onClick }) {
  const glowMap = {
    cyan:   { border: 'rgba(56,189,248,0.18)', bg: 'rgba(10,25,50,0.55)', shadow: 'rgba(56,189,248,0.08)' },
    green:  { border: 'rgba(0,245,212,0.18)',   bg: 'rgba(10,25,50,0.55)',  shadow: 'rgba(0,245,212,0.08)' },
    pink:   { border: 'rgba(255,102,162,0.18)', bg: 'rgba(10,25,50,0.55)', shadow: 'rgba(255,102,162,0.08)' },
    purple: { border: 'rgba(167,139,250,0.18)', bg: 'rgba(10,25,50,0.55)', shadow: 'rgba(167,139,250,0.08)' },
    orange: { border: 'rgba(251,146,60,0.18)',  bg: 'rgba(10,25,50,0.55)', shadow: 'rgba(251,146,60,0.08)' },
  };
  const c = glowMap[glow] || glowMap.cyan;
  return (
    <div onClick={onClick} style={{ borderRadius: 20, border: `1px solid ${c.border}`, background: c.bg, backdropFilter: 'blur(24px) saturate(180%)', WebkitBackdropFilter: 'blur(24px) saturate(180%)', boxShadow: `0 8px 32px ${c.shadow}, inset 0 1px 0 rgba(255,255,255,0.06)`, padding: '18px 16px', ...style }}>
      {children}
    </div>
  );
}

/* ---- Spec Info Row helper ---- */
function SpecRow({ label, value, accent }) {
  if (!value && value !== 0) return null;
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 10px', borderRadius: 8, background: 'rgba(255,255,255,0.02)', marginBottom: 3 }}>
      <span style={{ fontSize: '0.6rem', color: 'rgba(200,220,255,0.6)', fontWeight: 600 }}>{label}</span>
      <span style={{ fontSize: '0.62rem', color: accent || '#fff', fontWeight: 700, textAlign: 'right', maxWidth: '60%' }}>{value}</span>
    </div>
  );
}

/* ---- Section Header helper ---- */
function SectionHeader({ icon, title, color }) {
  return (
    <div style={{ fontSize: '0.68rem', fontWeight: 800, color: color || '#38bdf8', marginBottom: 8, marginTop: 16, display: 'flex', alignItems: 'center', gap: 6 }}>
      <span>{icon}</span> {title}
    </div>
  );
}

/* ---- Print Style Injection ---- */
const printCSS = `
@media print {
  body > *:not(.print-infoblatt-overlay) { display: none !important; }
  .print-infoblatt-overlay { position: static !important; background: #fff !important; }
  .print-infoblatt-overlay * { color: #000 !important; background: transparent !important; border-color: #ccc !important; }
  .print-infoblatt-content { max-width: 100% !important; width: 100% !important; max-height: none !important; overflow: visible !important; box-shadow: none !important; border: none !important; padding: 20px !important; }
  .no-print { display: none !important; }
  .print-infoblatt-content .spec-grid { break-inside: avoid; }
}
`;

export function SmartphoneDB() {
  const [devices, setDevices] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedBrand, setSelectedBrand] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [sortBy, setSortBy] = useState('name');
  const [viewMode, setViewMode] = useState('grid');
  const [detailTab, setDetailTab] = useState('details'); // 'details' | 'infoblatt'
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Inject print styles once
    if (!document.getElementById('smartphone-print-css')) {
      const style = document.createElement('style');
      style.id = 'smartphone-print-css';
      style.textContent = printCSS;
      document.head.appendChild(style);
    }
  }, []);

  useEffect(() => {
    setIsLoading(true);
    fetch('/api/smartphones')
      .then(r => r.json())
      .then(data => { setDevices(data.filter(d => d.active !== false)); setIsLoading(false); })
      .catch(() => { setDevices([]); setIsLoading(false); });
  }, []);

  const brands = useMemo(() => {
    const b = [...new Set(devices.map(d => d.brand))].sort();
    return ['all', ...b];
  }, [devices]);

  const categories = useMemo(() => {
    const c = [...new Set(devices.map(d => d.category))].sort();
    return ['all', ...c];
  }, [devices]);

  const filtered = useMemo(() => {
    let result = devices;
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(d => d.name.toLowerCase().includes(q) || d.brand.toLowerCase().includes(q));
    }
    if (selectedBrand !== 'all') result = result.filter(d => d.brand === selectedBrand);
    if (selectedCategory !== 'all') result = result.filter(d => d.category === selectedCategory);
    if (sortBy === 'name') result = [...result].sort((a, b) => a.name.localeCompare(b.name));
    else if (sortBy === 'price-asc') result = [...result].sort((a, b) => Math.min(...Object.values(a.prices || {})) - Math.min(...Object.values(b.prices || {})));
    else if (sortBy === 'price-desc') result = [...result].sort((a, b) => Math.min(...Object.values(b.prices || {})) - Math.min(...Object.values(a.prices || {})));
    else if (sortBy === 'year') result = [...result].sort((a, b) => (b.releaseYear || 0) - (a.releaseYear || 0));
    return result;
  }, [devices, search, selectedBrand, selectedCategory, sortBy]);

  const handlePrint = useCallback(() => {
    window.print();
  }, []);

  const openDevice = useCallback((device) => {
    setSelectedDevice(device);
    setDetailTab('details');
  }, []);

  /* =============== DETAIL MODAL =============== */
  const renderDetailModal = () => {
    if (!selectedDevice) return null;
    const d = selectedDevice;
    const cam = d.camera || {};
    const bat = d.battery || {};
    const conn = d.connectivity || {};
    const dim = d.dimensions || {};
    const dd = d.displayDetails || {};

    return (
      <div className={detailTab === 'infoblatt' ? 'print-infoblatt-overlay' : ''} style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(8px)' }} onClick={() => setSelectedDevice(null)}>
        <div className={detailTab === 'infoblatt' ? 'print-infoblatt-content' : ''} onClick={e => e.stopPropagation()} style={{ background: 'rgba(10,20,50,0.96)', border: '1px solid rgba(56,189,248,0.2)', borderRadius: 24, padding: '24px 20px', maxWidth: 520, width: '95%', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 24px 80px rgba(0,0,0,0.6)' }}>

          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
            <div>
              <div style={{ fontSize: '1.15rem', fontWeight: 900, color: '#fff' }}>{d.name}</div>
              <div style={{ fontSize: '0.62rem', color: 'rgba(200,220,255,0.65)', marginTop: 2 }}>{d.brand} · {d.category} · {d.releaseYear || d.releaseDate}</div>
            </div>
            <button className="no-print" onClick={() => setSelectedDevice(null)} style={{ background: 'none', border: 'none', color: '#ff006e', fontSize: '1.2rem', cursor: 'pointer' }}>✕</button>
          </div>

          {/* Tab Toggle */}
          <div className="no-print" style={{ display: 'flex', gap: 4, marginBottom: 16, background: 'rgba(255,255,255,0.03)', borderRadius: 12, padding: 3 }}>
            <button onClick={() => setDetailTab('details')}
              style={{ flex: 1, padding: '8px 12px', borderRadius: 10, border: 'none', background: detailTab === 'details' ? 'rgba(56,189,248,0.15)' : 'transparent', color: detailTab === 'details' ? '#38bdf8' : 'rgba(200,220,255,0.5)', fontSize: '0.68rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.2s' }}>
              📋 Details
            </button>
            <button onClick={() => setDetailTab('infoblatt')}
              style={{ flex: 1, padding: '8px 12px', borderRadius: 10, border: 'none', background: detailTab === 'infoblatt' ? 'rgba(0,245,212,0.15)' : 'transparent', color: detailTab === 'infoblatt' ? '#00f5d4' : 'rgba(200,220,255,0.5)', fontSize: '0.68rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.2s' }}>
              🖨️ Infoblatt
            </button>
          </div>

          {/* ===== DETAILS TAB ===== */}
          {detailTab === 'details' && (
            <>
              {/* Quick Specs Grid */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 12 }}>
                <div style={{ padding: '10px', borderRadius: 12, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                  <div style={{ fontSize: '0.48rem', color: 'rgba(200,220,255,0.6)', fontWeight: 700, textTransform: 'uppercase' }}>Betriebssystem</div>
                  <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#fff', marginTop: 2 }}>{d.os}</div>
                </div>
                <div style={{ padding: '10px', borderRadius: 12, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                  <div style={{ fontSize: '0.48rem', color: 'rgba(200,220,255,0.6)', fontWeight: 700, textTransform: 'uppercase' }}>Display</div>
                  <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#fff', marginTop: 2 }}>{d.display}</div>
                </div>
                {d.processor && (
                  <div style={{ padding: '10px', borderRadius: 12, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                    <div style={{ fontSize: '0.48rem', color: 'rgba(200,220,255,0.6)', fontWeight: 700, textTransform: 'uppercase' }}>Prozessor</div>
                    <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#fff', marginTop: 2 }}>{d.processor}</div>
                  </div>
                )}
                {d.ram && (
                  <div style={{ padding: '10px', borderRadius: 12, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                    <div style={{ fontSize: '0.48rem', color: 'rgba(200,220,255,0.6)', fontWeight: 700, textTransform: 'uppercase' }}>RAM</div>
                    <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#fff', marginTop: 2 }}>{d.ram}</div>
                  </div>
                )}
              </div>

              {/* Display Details */}
              {(dd.resolution || dd.refreshRate || dd.brightness) && (
                <>
                  <SectionHeader icon="🖥️" title="Display Details" color="#38bdf8" />
                  <SpecRow label="Auflösung" value={dd.resolution} />
                  <SpecRow label="Bildwiederholrate" value={dd.refreshRate} />
                  <SpecRow label="Helligkeit" value={dd.brightness} />
                  <SpecRow label="Schutzglas" value={dd.protection} />
                </>
              )}

              {/* Camera */}
              {(cam.main || cam.front) && (
                <>
                  <SectionHeader icon="📸" title="Kamera" color="#a78bfa" />
                  <SpecRow label="Hauptkamera" value={cam.main} accent="#a78bfa" />
                  {cam.ultrawide && <SpecRow label="Ultraweitwinkel" value={cam.ultrawide} />}
                  {cam.telephoto && <SpecRow label="Teleobjektiv" value={cam.telephoto} />}
                  {cam.telephoto2 && <SpecRow label="Teleobjektiv 2" value={cam.telephoto2} />}
                  {cam.macro && <SpecRow label="Makro" value={cam.macro} />}
                  <SpecRow label="Frontkamera" value={cam.front} accent="#a78bfa" />
                </>
              )}

              {/* Battery */}
              {(bat.capacity || bat.charging) && (
                <>
                  <SectionHeader icon="🔋" title="Akku" color="#00f5d4" />
                  <SpecRow label="Kapazität" value={bat.capacity} accent="#00f5d4" />
                  <SpecRow label="Schnellladen" value={bat.charging} />
                  <SpecRow label="Kabelloses Laden" value={bat.wireless} />
                </>
              )}

              {/* Connectivity */}
              {(conn.wifi || conn.bluetooth) && (
                <>
                  <SectionHeader icon="📶" title="Konnektivität" color="#fb923c" />
                  <SpecRow label="5G" value={conn['5g'] ? '✅ Ja' : (conn['5g'] === false ? '❌ Nein' : null)} />
                  <SpecRow label="WLAN" value={conn.wifi} />
                  <SpecRow label="Bluetooth" value={conn.bluetooth} />
                  <SpecRow label="NFC" value={conn.nfc ? '✅ Ja' : (conn.nfc === false ? '❌ Nein' : null)} />
                  <SpecRow label="USB" value={conn.usb} />
                </>
              )}

              {/* Dimensions & Build */}
              {(dim.height || d.waterResistance) && (
                <>
                  <SectionHeader icon="📐" title="Abmessungen & Gehäuse" color="#38bdf8" />
                  {dim.height && <SpecRow label="Maße (H×B×T)" value={`${dim.height} × ${dim.width} × ${dim.depth} mm`} />}
                  {dim.weight && <SpecRow label="Gewicht" value={`${dim.weight} g`} />}
                  <SpecRow label="Wasserschutz" value={d.waterResistance} />
                  <SpecRow label="Biometrie" value={d.biometrics} />
                  <SpecRow label="SIM-Typ" value={d.simType} />
                </>
              )}

              {/* Special Features */}
              {d.specialFeatures && d.specialFeatures.length > 0 && (
                <>
                  <SectionHeader icon="✨" title="Besondere Features" color="#ff006e" />
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 8 }}>
                    {d.specialFeatures.map((f, i) => (
                      <span key={i} style={{ padding: '4px 10px', borderRadius: 8, background: 'rgba(255,0,110,0.06)', border: '1px solid rgba(255,0,110,0.15)', fontSize: '0.58rem', color: 'rgba(200,220,255,0.82)' }}>⭐ {f}</span>
                    ))}
                  </div>
                </>
              )}

              {/* Colors */}
              {d.colors && (
                <div style={{ marginBottom: 14, marginTop: 12 }}>
                  <div style={{ fontSize: '0.52rem', fontWeight: 700, color: 'rgba(200,220,255,0.65)', marginBottom: 6, textTransform: 'uppercase' }}>Verfügbare Farben</div>
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    {d.colors.map(c => (
                      <span key={c} style={{ padding: '4px 10px', borderRadius: 8, background: 'rgba(56,189,248,0.06)', border: '1px solid rgba(56,189,248,0.15)', fontSize: '0.6rem', color: 'rgba(200,220,255,0.82)' }}>{c}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Prices by Storage */}
              {d.storage && d.prices && (
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: '0.52rem', fontWeight: 700, color: 'rgba(200,220,255,0.65)', marginBottom: 6, textTransform: 'uppercase' }}>Preise nach Speicher</div>
                  {d.storage.map(s => (
                    <div key={s} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 10px', borderRadius: 10, background: 'rgba(255,255,255,0.02)', marginBottom: 4, border: '1px solid rgba(255,255,255,0.04)' }}>
                      <span style={{ fontSize: '0.7rem', color: 'rgba(200,220,255,0.82)', fontWeight: 600 }}>{s >= 1024 ? `${s/1024} TB` : `${s} GB`}</span>
                      <div style={{ textAlign: 'right' }}>
                        <span style={{ fontSize: '0.78rem', fontWeight: 800, color: '#00f5d4' }}>{fmtEuro(d.prices[s])}</span>
                        {d.monthlyPrices?.[s] > 0 && <span style={{ fontSize: '0.52rem', color: 'rgba(200,220,255,0.6)', marginLeft: 8 }}>o. {fmtEuro(d.monthlyPrices[s])}/mtl.</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Meta/EAN */}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                {d.warranty && <span style={{ fontSize: '0.5rem', color: 'rgba(200,220,255,0.5)' }}>🛡️ {d.warranty} Garantie</span>}
                {d.ean && <span style={{ fontSize: '0.5rem', color: 'rgba(200,220,255,0.5)' }}>📦 EAN: {d.ean}</span>}
              </div>

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                <button onClick={() => { window.location.hash = '#/konfigurator'; setSelectedDevice(null); }}
                  style={{ flex: 1, padding: '12px', borderRadius: 12, border: 'none', background: 'linear-gradient(135deg, #38bdf8, #0284c7)', color: '#fff', fontSize: '0.72rem', fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 4px 20px rgba(56,189,248,0.3)' }}>
                  📋 Im Konfigurator öffnen
                </button>
                <button onClick={() => setDetailTab('infoblatt')}
                  style={{ padding: '12px 16px', borderRadius: 12, border: '1px solid rgba(0,245,212,0.3)', background: 'rgba(0,245,212,0.06)', color: '#00f5d4', fontSize: '0.72rem', fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit' }}>
                  🖨️
                </button>
              </div>
            </>
          )}

          {/* ===== INFOBLATT TAB ===== */}
          {detailTab === 'infoblatt' && (
            <>
              {/* Print-Ready Infoblatt */}
              <div style={{ borderRadius: 16, border: '1px solid rgba(56,189,248,0.15)', padding: '20px', background: 'rgba(10,20,50,0.5)' }}>
                {/* Title Block */}
                <div style={{ textAlign: 'center', marginBottom: 18, paddingBottom: 14, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                  <div style={{ fontSize: '1.3rem', fontWeight: 900, color: '#fff' }}>{d.name}</div>
                  <div style={{ fontSize: '0.72rem', color: 'rgba(200,220,255,0.7)', marginTop: 4 }}>{d.brand} · {d.category} · {d.releaseYear || d.releaseDate}</div>
                  {d.ean && <div style={{ fontSize: '0.55rem', color: 'rgba(200,220,255,0.45)', marginTop: 4 }}>EAN: {d.ean}</div>}
                </div>

                {/* Two Column Spec Layout */}
                <div className="spec-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  {/* Left Column */}
                  <div>
                    <div style={{ fontSize: '0.65rem', fontWeight: 800, color: '#38bdf8', marginBottom: 6 }}>📱 Allgemein</div>
                    <SpecRow label="Betriebssystem" value={d.os} />
                    <SpecRow label="Prozessor" value={d.processor} />
                    <SpecRow label="RAM" value={d.ram} />
                    <SpecRow label="Speicher" value={d.storage?.map(s => s >= 1024 ? `${s/1024} TB` : `${s} GB`).join(' / ')} />

                    <div style={{ fontSize: '0.65rem', fontWeight: 800, color: '#38bdf8', marginBottom: 6, marginTop: 12 }}>🖥️ Display</div>
                    <SpecRow label="Typ/Größe" value={d.display} />
                    <SpecRow label="Auflösung" value={dd.resolution} />
                    <SpecRow label="Hz" value={dd.refreshRate} />
                    <SpecRow label="Helligkeit" value={dd.brightness} />
                    <SpecRow label="Schutzglas" value={dd.protection} />

                    <div style={{ fontSize: '0.65rem', fontWeight: 800, color: '#a78bfa', marginBottom: 6, marginTop: 12 }}>📸 Kamera</div>
                    <SpecRow label="Hauptkamera" value={cam.main} />
                    {cam.ultrawide && <SpecRow label="Ultraweitw." value={cam.ultrawide} />}
                    {cam.telephoto && <SpecRow label="Tele" value={cam.telephoto} />}
                    {cam.telephoto2 && <SpecRow label="Tele 2" value={cam.telephoto2} />}
                    {cam.macro && <SpecRow label="Makro" value={cam.macro} />}
                    <SpecRow label="Front" value={cam.front} />
                  </div>

                  {/* Right Column */}
                  <div>
                    <div style={{ fontSize: '0.65rem', fontWeight: 800, color: '#00f5d4', marginBottom: 6 }}>🔋 Akku</div>
                    <SpecRow label="Kapazität" value={bat.capacity} />
                    <SpecRow label="Laden" value={bat.charging} />
                    <SpecRow label="Kabellos" value={bat.wireless} />

                    <div style={{ fontSize: '0.65rem', fontWeight: 800, color: '#fb923c', marginBottom: 6, marginTop: 12 }}>📶 Konnektivität</div>
                    <SpecRow label="5G" value={conn['5g'] ? 'Ja' : (conn['5g'] === false ? 'Nein' : null)} />
                    <SpecRow label="WLAN" value={conn.wifi} />
                    <SpecRow label="Bluetooth" value={conn.bluetooth} />
                    <SpecRow label="NFC" value={conn.nfc ? 'Ja' : (conn.nfc === false ? 'Nein' : null)} />
                    <SpecRow label="USB" value={conn.usb} />

                    <div style={{ fontSize: '0.65rem', fontWeight: 800, color: '#38bdf8', marginBottom: 6, marginTop: 12 }}>📐 Gehäuse</div>
                    {dim.height && <SpecRow label="Maße" value={`${dim.height}×${dim.width}×${dim.depth} mm`} />}
                    {dim.weight && <SpecRow label="Gewicht" value={`${dim.weight} g`} />}
                    <SpecRow label="Wasserschutz" value={d.waterResistance} />
                    <SpecRow label="Biometrie" value={d.biometrics} />
                    <SpecRow label="SIM" value={d.simType} />

                    {d.specialFeatures && d.specialFeatures.length > 0 && (
                      <>
                        <div style={{ fontSize: '0.65rem', fontWeight: 800, color: '#ff006e', marginBottom: 6, marginTop: 12 }}>✨ Features</div>
                        {d.specialFeatures.map((f, i) => (
                          <div key={i} style={{ fontSize: '0.55rem', color: 'rgba(200,220,255,0.75)', padding: '2px 0' }}>• {f}</div>
                        ))}
                      </>
                    )}
                  </div>
                </div>

                {/* Prices Table */}
                {d.storage && d.prices && (
                  <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                    <div style={{ fontSize: '0.65rem', fontWeight: 800, color: '#00f5d4', marginBottom: 8 }}>💰 Preise</div>
                    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${d.storage.length}, 1fr)`, gap: 8 }}>
                      {d.storage.map(s => (
                        <div key={s} style={{ textAlign: 'center', padding: '10px 8px', borderRadius: 12, background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
                          <div style={{ fontSize: '0.62rem', fontWeight: 700, color: 'rgba(200,220,255,0.7)', marginBottom: 4 }}>{s >= 1024 ? `${s/1024} TB` : `${s} GB`}</div>
                          <div style={{ fontSize: '0.85rem', fontWeight: 900, color: '#00f5d4' }}>{fmtEuro(d.prices[s])}</div>
                          {d.monthlyPrices?.[s] > 0 && <div style={{ fontSize: '0.5rem', color: 'rgba(200,220,255,0.55)', marginTop: 2 }}>{fmtEuro(d.monthlyPrices[s])}/mtl.</div>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Colors + Warranty Footer */}
                <div style={{ marginTop: 14, paddingTop: 10, borderTop: '1px solid rgba(255,255,255,0.06)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 8 }}>
                  {d.colors && (
                    <div>
                      <div style={{ fontSize: '0.5rem', color: 'rgba(200,220,255,0.5)', marginBottom: 3 }}>Farben</div>
                      <div style={{ fontSize: '0.58rem', color: 'rgba(200,220,255,0.72)' }}>{d.colors.join(', ')}</div>
                    </div>
                  )}
                  {d.warranty && <div style={{ fontSize: '0.52rem', color: 'rgba(200,220,255,0.45)' }}>🛡️ {d.warranty} Garantie</div>}
                </div>
              </div>

              {/* Print Button */}
              <div className="no-print" style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                <button onClick={() => setDetailTab('details')}
                  style={{ padding: '12px 16px', borderRadius: 12, border: '1px solid rgba(56,189,248,0.3)', background: 'rgba(56,189,248,0.06)', color: '#38bdf8', fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                  ← Zurück
                </button>
                <button onClick={handlePrint}
                  style={{ flex: 1, padding: '12px', borderRadius: 12, border: 'none', background: 'linear-gradient(135deg, #00f5d4, #0d9488)', color: '#fff', fontSize: '0.72rem', fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 4px 20px rgba(0,245,212,0.25)' }}>
                  🖨️ Infoblatt drucken
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div style={{ minHeight: '100vh', color: '#fff', fontFamily: "'Poppins', system-ui, sans-serif" }}>
      <div style={{ maxWidth: 900, margin: '0 auto', padding: '12px 16px 100px' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 900, background: 'linear-gradient(135deg, #38bdf8, #00f5d4)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>📱 Smartphone Datenbank</div>
          <div style={{ fontSize: '0.68rem', color: 'rgba(200,220,255,0.72)', marginTop: 4 }}>{devices.length} Geräte verfügbar</div>
        </div>

        {/* Filter Bar */}
        <GlassCard glow="cyan" style={{ marginBottom: 16 }}>
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="🔍 Gerät suchen..."
            style={{ width: '100%', padding: '12px 14px', borderRadius: 14, border: '1px solid rgba(56,189,248,0.2)', background: 'rgba(56,189,248,0.04)', color: '#fff', fontSize: '0.82rem', fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box', marginBottom: 12 }} />
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 140 }}>
              <div style={{ fontSize: '0.52rem', fontWeight: 700, color: 'rgba(200,220,255,0.65)', marginBottom: 4, textTransform: 'uppercase' }}>Marke</div>
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {brands.map(b => (
                  <button key={b} onClick={() => setSelectedBrand(b)}
                    style={{ padding: '5px 12px', borderRadius: 8, border: `1px solid ${selectedBrand === b ? 'rgba(56,189,248,0.4)' : 'rgba(255,255,255,0.06)'}`, background: selectedBrand === b ? 'rgba(56,189,248,0.12)' : 'rgba(255,255,255,0.02)', color: selectedBrand === b ? '#38bdf8' : 'rgba(200,220,255,0.72)', fontSize: '0.62rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                    {b === 'all' ? 'Alle' : b}
                  </button>
                ))}
              </div>
            </div>
            <div style={{ flex: 1, minWidth: 140 }}>
              <div style={{ fontSize: '0.52rem', fontWeight: 700, color: 'rgba(200,220,255,0.65)', marginBottom: 4, textTransform: 'uppercase' }}>Kategorie</div>
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {categories.map(c => (
                  <button key={c} onClick={() => setSelectedCategory(c)}
                    style={{ padding: '5px 12px', borderRadius: 8, border: `1px solid ${selectedCategory === c ? 'rgba(0,245,212,0.4)' : 'rgba(255,255,255,0.06)'}`, background: selectedCategory === c ? 'rgba(0,245,212,0.12)' : 'rgba(255,255,255,0.02)', color: selectedCategory === c ? '#00f5d4' : 'rgba(200,220,255,0.72)', fontSize: '0.62rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                    {c === 'all' ? 'Alle' : c}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div style={{ marginTop: 10 }}>
            <div style={{ fontSize: '0.52rem', fontWeight: 700, color: 'rgba(200,220,255,0.65)', marginBottom: 4, textTransform: 'uppercase' }}>Sortierung</div>
            <div style={{ display: 'flex', gap: 4 }}>
              {[{id:'name',label:'Name'},{id:'price-asc',label:'Preis ↑'},{id:'price-desc',label:'Preis ↓'},{id:'year',label:'Neueste'}].map(s => (
                <button key={s.id} onClick={() => setSortBy(s.id)}
                  style={{ padding: '5px 12px', borderRadius: 8, border: `1px solid ${sortBy === s.id ? 'rgba(167,139,250,0.4)' : 'rgba(255,255,255,0.06)'}`, background: sortBy === s.id ? 'rgba(167,139,250,0.12)' : 'rgba(255,255,255,0.02)', color: sortBy === s.id ? '#a78bfa' : 'rgba(200,220,255,0.72)', fontSize: '0.62rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                  {s.label}
                </button>
              ))}
            </div>
          </div>
        </GlassCard>

        {/* Device Detail Modal */}
        {renderDetailModal()}

        {/* View Toggle */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div style={{ fontSize: '0.62rem', fontWeight: 700, color: 'rgba(200,220,255,0.6)' }}>{filtered.length} Ergebnis{filtered.length !== 1 ? 'se' : ''}</div>
          <div style={{ display: 'flex', background: 'rgba(255,255,255,0.04)', borderRadius: 10, padding: 3, border: '1px solid rgba(255,255,255,0.06)' }}>
            <button onClick={() => setViewMode('grid')}
              style={{ padding: '6px 12px', borderRadius: 8, border: 'none', background: viewMode === 'grid' ? 'rgba(56,189,248,0.15)' : 'transparent', color: viewMode === 'grid' ? '#38bdf8' : 'rgba(200,220,255,0.5)', fontSize: '0.65rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 5, transition: 'all 0.2s' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>
              Kacheln
            </button>
            <button onClick={() => setViewMode('list')}
              style={{ padding: '6px 12px', borderRadius: 8, border: 'none', background: viewMode === 'list' ? 'rgba(56,189,248,0.15)' : 'transparent', color: viewMode === 'list' ? '#38bdf8' : 'rgba(200,220,255,0.5)', fontSize: '0.65rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 5, transition: 'all 0.2s' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>
              Liste
            </button>
          </div>
        </div>

        {/* Device Grid / List */}
        {isLoading ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12 }}>
            {[1,2,3,4,5,6,7,8].map(i => (
               <GlassCard key={i} glow="cyan" style={{ opacity: 0.4 }}>
                 <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                   <div style={{ width: 44, height: 44, borderRadius: 12, background: 'rgba(255,255,255,0.05)' }} />
                   <div style={{ flex: 1 }}>
                     <div style={{ height: 14, background: 'rgba(255,255,255,0.1)', borderRadius: 4, marginBottom: 6 }} />
                     <div style={{ height: 10, width: '60%', background: 'rgba(255,255,255,0.05)', borderRadius: 4 }} />
                   </div>
                 </div>
                 <div style={{ height: 32, background: 'rgba(255,255,255,0.05)', borderRadius: 6, marginTop: 16 }} />
                 <div style={{ height: 12, width: '40%', background: 'rgba(255,255,255,0.05)', borderRadius: 4, marginTop: 8 }} />
               </GlassCard>
            ))}
          </div>
        ) : viewMode === 'grid' ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12 }}>
            {filtered.map(device => {
              const minPrice = device.prices ? Math.min(...Object.values(device.prices)) : 0;
              return (
                <GlassCard key={device.id} glow="cyan" style={{ cursor: 'pointer', transition: 'transform 0.2s, box-shadow 0.2s' }} onClick={() => openDevice(device)}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                    <div style={{ width: 44, height: 44, borderRadius: 12, background: 'rgba(56,189,248,0.06)', border: '1px solid rgba(56,189,248,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.4rem', flexShrink: 0 }}>📱</div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: '0.78rem', fontWeight: 800, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{device.name}</div>
                      <div style={{ fontSize: '0.55rem', color: 'rgba(200,220,255,0.65)' }}>{device.brand} · {device.category}</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                    <div>
                      <div style={{ fontSize: '0.5rem', color: 'rgba(200,220,255,0.55)', fontWeight: 700, textTransform: 'uppercase' }}>{device.display}</div>
                      <div style={{ fontSize: '0.5rem', color: 'rgba(200,220,255,0.55)' }}>{device.storage ? device.storage.map(s => s >= 1024 ? `${s/1024}TB` : `${s}GB`).join(' · ') : ''}</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: '0.95rem', fontWeight: 900, color: '#00f5d4' }}>ab {fmtEuro(minPrice)}</div>
                      <div style={{ fontSize: '0.5rem', color: 'rgba(200,220,255,0.55)' }}>{device.releaseYear}</div>
                    </div>
                  </div>
                  {/* Quick spec badges */}
                  <div style={{ display: 'flex', gap: 3, marginTop: 8, flexWrap: 'wrap' }}>
                    {device.processor && <span style={{ padding: '2px 7px', borderRadius: 6, background: 'rgba(167,139,250,0.06)', border: '1px solid rgba(167,139,250,0.12)', fontSize: '0.44rem', color: 'rgba(200,220,255,0.65)' }}>⚡ {device.processor.split(' ').slice(0,2).join(' ')}</span>}
                    {device.camera?.main && <span style={{ padding: '2px 7px', borderRadius: 6, background: 'rgba(255,0,110,0.06)', border: '1px solid rgba(255,0,110,0.12)', fontSize: '0.44rem', color: 'rgba(200,220,255,0.65)' }}>📸 {device.camera.main.split(' ').slice(0,1).join('')}</span>}
                    {device.battery?.capacity && <span style={{ padding: '2px 7px', borderRadius: 6, background: 'rgba(0,245,212,0.06)', border: '1px solid rgba(0,245,212,0.12)', fontSize: '0.44rem', color: 'rgba(200,220,255,0.65)' }}>🔋 {device.battery.capacity}</span>}
                  </div>
                </GlassCard>
              );
            })}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {filtered.map(device => {
              const minPrice = device.prices ? Math.min(...Object.values(device.prices)) : 0;
              return (
                <GlassCard key={device.id} glow="cyan" style={{ cursor: 'pointer', padding: '12px 16px', transition: 'transform 0.2s, box-shadow 0.2s' }} onClick={() => openDevice(device)}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                    <div style={{ width: 38, height: 38, borderRadius: 10, background: 'rgba(56,189,248,0.06)', border: '1px solid rgba(56,189,248,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem', flexShrink: 0 }}>📱</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '0.78rem', fontWeight: 800, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{device.name}</div>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 2 }}>
                        <span style={{ fontSize: '0.52rem', color: 'rgba(200,220,255,0.65)' }}>{device.brand}</span>
                        <span style={{ fontSize: '0.52rem', color: 'rgba(200,220,255,0.4)' }}>·</span>
                        <span style={{ fontSize: '0.52rem', color: 'rgba(200,220,255,0.65)' }}>{device.category}</span>
                        <span style={{ fontSize: '0.52rem', color: 'rgba(200,220,255,0.4)' }}>·</span>
                        <span style={{ fontSize: '0.52rem', color: 'rgba(200,220,255,0.55)' }}>{device.display}</span>
                        {device.camera?.main && <><span style={{ fontSize: '0.52rem', color: 'rgba(200,220,255,0.4)' }}>·</span><span style={{ fontSize: '0.52rem', color: 'rgba(200,220,255,0.55)' }}>📸 {device.camera.main.split(' ')[0]}</span></>}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontSize: '0.88rem', fontWeight: 900, color: '#00f5d4' }}>ab {fmtEuro(minPrice)}</div>
                      <div style={{ fontSize: '0.48rem', color: 'rgba(200,220,255,0.5)' }}>{device.releaseYear}{device.colors ? ` · ${device.colors.length} Farben` : ''}</div>
                    </div>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(200,220,255,0.35)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><polyline points="9 18 15 12 9 6"/></svg>
                  </div>
                </GlassCard>
              );
            })}
          </div>
        )}
        {!isLoading && filtered.length === 0 && (
          <div style={{ textAlign: 'center', padding: '48px 0', color: 'rgba(200,220,255,0.5)', fontSize: '0.82rem' }}>Keine Geräte gefunden</div>
        )}
      </div>
    </div>
  );
}

export default SmartphoneDB;
